import { ISeriesValue } from '../ISeriesValue';
import { TScenarioPeriodType } from '../TScenarioPeriodType';
import { IDates } from '../IDates';

export type MethodType = 'Random' | 'RandomEF' | 'RandomEFPlusComp' | 'Cluster' | 'Cluster-Random' | 'Cluster-Centroid';

export interface TenorPoint {
	tenor: string;
	value: number;
}

export type Matrix = number[][];

export interface Drawdown2 {
	maxDrawdownLength?: string;
	maxDrawdownRecoveryLength?: string;
	maxDrawdownUnderwaterPeriod?: string;
	currentDrawdown?: number;
	currentDrawdownDate?: string;
	maxDrawdown?: number;
	maxDrawdownDate?: string;
	drawdownSeries?: ISeriesValue[];
}

export interface AssetMatrix {
	matrix: Matrix;
	percentMatrix?: string[][];
	portfolioNames: string[];
}

export interface ForecastReturn {
	probability: number;
	annualisedExpectedReturn: number;
	cumulativeFutureReturn: ISeriesValue[];
	annualisedFutureReturn: ISeriesValue[];
	futureFundValue: ISeriesValue[];
}

export interface NegativePositiveRun {
	numberOfDataPoints: number;
	startDate?: string;
	endDate?: string;
}

export enum PriipsPeriod {
	Monthly,
	BiMonthly,
	Weekly,
	Daily,
}

export interface FundResult {
	name?: string;

	// alphaBeta.ts
	alpha?: number;
	annualisedAlpha?: number;
	beta?: number;
	jensenAlpha?: number;
	annualisedJensenAlpha?: number;

	// battingAverage.ts
	battingAvg?: number;

	// bestWorstPeriod.ts
	bestPeriod?: ISeriesValue[];
	worstPeriod?: ISeriesValue[];

	// biasRatio.ts
	biasPop?: number;
	biasSam?: number;

	// calendarYearReturns.ts
	calendarYearReturns?: ISeriesValue[];

	// calmarRatio.ts
	calmarRatio?: number;

	// deviation.ts
	gainDeviationMAR?: number;
	gainDeviationBenchmark?: number;
	gainDeviationRF?: number;
	lossDeviationMAR?: number;
	lossDeviationBenchmark?: number;
	lossDeviationRF?: number;

	// drawdown.ts
	drawdown2?: Drawdown2;
	averageAnnualMaxDrawdown?: number;
	drawdown2Benchmark?: Drawdown2;
	drawdown2RF?: Drawdown2;

	// excessReturns.ts
	excessReturns?: ISeriesValue[];
	excessReturnsOverRF?: ISeriesValue[];
	excessReturnsOverRFAnnualised?: number;
	excessReturnsOverRFAnnualisedDiff?: number;
	excessReturnsAnnualised?: number;
	excessReturnsAnnualisedDiff?: number;

	// informationRatio.ts
	informationRatioPop?: number;
	informationRatioPopDiff?: number;
	informationRatioSam?: number;
	informationRatioSamDiff?: number;

	// kurtosis.ts
	kurtosis?: number;

	// omegaRatio.ts
	omegaRatio?: number;

	// trailingReturns.ts
	trailingYearReturns?: ISeriesValue[];
	trailingQuarterReturnsCumulative?: ISeriesValue[];
	trailingQuarterReturns?: ISeriesValue[];

	// volatility.ts
	trailingVolatilityPop?: ISeriesValue[];
	trailingVolatilitySam?: ISeriesValue[];

	// positiveNegativePeriods.ts
	numOfPosPeriods?: number;
	numOfNegPeriods?: number;
	totalNumOfPeriods?: number;
	longestPosPeriod?: NegativePositiveRun[];
	longestNegPeriod?: NegativePositiveRun[];

	// return.ts
	cumulativeReturn?: number;
	annualisedReturn?: number;

	// riskFreeReturns.ts
	riskFreeReturns?: ISeriesValue[];

	// rollingAverage.ts
	rollingAverage?: ISeriesValue[];

	// sharpeRatio.ts
	sharpeRatioPop?: number;
	sharpeRatioSam?: number;
	sharpeRatioPopAnnualisedDiff?: number;
	sharpeRatioSamAnnualisedDiff?: number;
	sharpeRatioPopSimple?: number;
	sharpeRatioSamSimple?: number;

	// skewness.ts
	skewness?: number;

	// sortinoRatio.ts
	sortinoRatio?: number;
	sortinoRatioAnnualisedDiff?: number;

	// sterlingRatio.ts
	sterlingRatio?: number;

	// trackingError.ts
	trackingErrorPop?: number;
	annualisedTrackingErrorPop?: number;
	trackingErrorSam?: number;
	annualisedTrackingErrorSam?: number;

	// treynorRatio.ts
	treynorRatio?: number;
	treynorRatioAnnualisedDiff?: number;

	// upDownMarketReturn.ts
	downsideMarketCaptRatio?: number;
	upsideMarketCaptRatio?: number;
	downMarketReturn?: number;
	upMarketReturn?: number;

	// utils/stats.ts
	StandardDeviationPop?: number;
	annualisedStandardDeviationPop?: number;
	StandardDeviationSam?: number;
	annualisedStandardDeviationSam?: number;

	// covariance_correlationMatrix.ts
	RSquared?: number;

	// seriesSplice.ts
	seriesSplice?: ISeriesValue[];

	error?: string[];
}

export interface PortfolioResult {
	// contributionToRisk.ts
	MCTR?: AssetMatrix;
	absCTR?: AssetMatrix;

	// covariance_correlationMatrix.ts
	correlation?: AssetMatrix;
	covariance?: AssetMatrix;
	RSquared?: AssetMatrix;

	// excessReturns.ts
	excessReturnsCorrelation?: AssetMatrix;

	// portfolioReturns.ts
	trailingReturns?: ISeriesValue[];

	// forecast.ts
	forecast?: ForecastReturn[];

	compositeFund?: FundResult;

	error?: string[];
}

export interface BatchResult extends BaseResult {
	results?: IPortfolioStatisticsResult[];
}

export interface IPortfolioStatisticsResult extends BaseResult {
	id?: string;
	periodType?: TScenarioPeriodType;
	funds?: FundResult[];
	portfolio?: PortfolioResult;
}

export interface PriipsResult extends BaseResult {
	calculationStatus?: string;
	result?: PriipsScores;
	missing?: string[];
	inconsistent?: string[];
	notes?: PriipsNote[];
	dataIdentity?: PriipsDataString;
	calculations?: PriipsCalculation;
}

export interface PriipsData {
	dataPeriod?: PriipsPeriod;
	periodsInYear?: number;
}

export interface PriipsDataString {
	dataPeriod?: string;
	periodsInYear?: number;
}

export interface PriipsScores {
	PriipsCategory?: number;
	MRMScore?: number;
	CRMScore?: number | string;
	SRIScore?: number;
	rateCurve?: TenorPoint[];
	PerformanceScenarios?: PriipsPerformanceScenarios;
}

export interface PriipsNote {
	code?: string;
	narrative?: string;
}

export interface PriipsCalculation {
	MRM?: MRMCalculation;
	CRM?: CRMBlock;
}

export interface MRMCalculation {
	naturalLogReturns?: ISeriesValue[];
	M0?: number;
	M1?: number;
	M2?: number;
	SD?: number;
	M3?: number;
	skew?: number;
	M4?: number;
	excessKurtosis?: number;
	VaRReturnSpace?: number;
	MRM3SimulationSets?: MRM3SimulationSet[];
	VaRPriceSpace?: number;
	provisionalVEV?: number;
	nonMarketComponentWeighting?: number;
	nonMarketComponentVEV?: number;
	marketComponentVEV?: number;
	VEV?: number;
	MRM3CurveSimulationSets?: MRM3CurveSimulationSet[];
}

export interface CRMBlock {
	CRMDirect?: CRMCalculation;
	CRMLookThrough?: CRMLookThrough;
	CRMCascade?: CRMCascade;
	provisionalCRMScore?: number | string;
	CRMScore?: number | string;
}

export interface CRMCalculation {
	weighting?: number;
	CRMScore?: number | string;
	provisionalCRMScore?: number | string;
	priipsCQSs?: CreditAssessmentCQS[];
	medianPriipsCQS?: CreditAssessmentCQS;
	obligorCQSs?: CreditAssessmentCQS[];
	medianObligorCQS?: CreditAssessmentCQS;
	guarantorCQSs?: CreditAssessmentCQS[];
	medianGuarantorCQS?: CreditAssessmentCQS;
	provisionalCQS?: number;
	adjustedCQS?: number;
}

export interface CreditAssessmentCQS {
	requestIndex?: number;
	instituteCode?: string;
	scaleCode?: string;
	rating?: string;
	hasAccountedForMaturityOrRHP?: boolean;
	CQS?: number;
}

export interface CRMLookThrough {
	provisionalCRMScore?: number | string;
	weightedCQS?: number;
	underlyingCRMCalculations?: CRMCalculation[];
}

export interface CRMCascade {
	CRMDirect?: CRMCalculation;
	CRMLookThrough?: CRMLookThrough;
	provisionalCRMScore?: number | string;
}

export interface MRM3CurveSimulationSet {
	yearsForScenario?: number;
	periodsForScenario?: number;
	MRM3CurveSimulations?: { [key: string]: number[] };
	simulatedCurve?: TenorPoint[];
	unfavourable?: ScenarioSet;
	moderate?: ScenarioSet;
	favourable?: ScenarioSet;
	stressed?: ScenarioSet;
}

export interface MRM3SimulationSet {
	yearsForScenario?: number;
	periodsForScenario?: number;
	MRM3Simulations?: MRM3Simulation[];
	VaRPriceSpace?: number;
	unfavourable?: ScenarioSet;
	moderate?: ScenarioSet;
	favourable?: ScenarioSet;
	stressed?: ScenarioSet;
}

export interface MRM3Simulation {
	randomPeriodData?: number[];
	sumSimulatedData?: number;
	eReturnRiskNeutral?: number;
	eReturnMeasured?: number;
	adjustedSimulatedReturn?: number;
	expSimulatedReturn?: number;
	adjustedScenarioReturn?: number;
	expScenarioReturn?: number;
}

export interface PriipsPerformanceScenarios {
	narrative?: string[];
	denominatedCurrency?: string;
	currencyUnit?: number;
	scenarios?: PerformanceScenario[];
}

export interface PerformanceScenario {
	yearsForScenario?: number;
	periodsForScenario?: number;
	unfavourable?: ScenarioSet;
	moderate?: ScenarioSet;
	favourable?: ScenarioSet;
	stressed?: ScenarioSet;
}

export interface ScenarioSet {
	stressedVolatility?: number;
	adjustedReturns?: ISeriesValue[];
	adjRetSD?: number;
	adjRetMean?: number;
	stressedSimulations?: MRM3Simulation[];
	scenarioCurve?: TenorPoint[];
	scenarioReturn?: number;
	grossReturnAmount?: number;
}

export interface PriipsECAIsResult extends BaseResult {
	ECAIs?: ECAI[];
}

export interface ECAI {
	institute?: string;
	instituteCode?: string;
}

export interface PriipsECAIScalesResult extends BaseResult {
	instituteCode?: string;
	ECAIScales?: ECAIScale[];
}

export interface ECAIScale {
	scale?: string;
	scaleCode?: string;
}

export interface PriipsECAIScaleRatingsResult extends BaseResult {
	instituteCode?: string;
	scaleCode?: string;
	ratings?: string[];
}

export interface BaseResult {
	error?: any;
	status?: number;
}

export interface EFResult extends BaseResult {
	filteredFunds?: string[];
	portfolioEF?: PortfolioOfFunds[];
	notes?: string[];
}

export interface PortfolioOfFunds {
	portfolioName?: string;
	generationMethod?: MethodType;
	funds?: PortfolioFund[];
	risk?: number;
	reward?: number;
}

export interface PortfolioFund {
	name?: string;
	weighting?: number;
}
